/**
 * Contexto de Funcionários exclusivo para o módulo Gestão da Formação.
 * Usa sempre o município "Gestão da Formação" (MUNICIPIO_FORMACAO_ID).
 */
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { MUNICIPIO_FORMACAO_ID } from '@/lib/municipios';

export interface FuncionarioFormacao {
  id: string;
  nome: string;
  carreira: string;
  sexo: 'M' | 'F';
  unidadeOrganica: string;
  subunidadeOrganica: string;
  email: string;
  telefone: string;
  status: string;
  categoria: string;
  dataAlteracaoCategoria: string;
  superiorHierarquico?: string;
}

export interface UnidadeOrganicaGF {
  id: string;
  nome: string;
  tipo: string;
}

export interface NovoFuncionarioFormacaoInput {
  nome: string;
  carreira: string;
  sexo: 'M' | 'F';
  unidadeOrganica: string;
  subunidadeOrganica: string;
  email: string;
  telefone: string;
  status: string;
  dataAlteracaoCategoria?: string;
}

interface FuncionariosFormacaoContextValue {
  funcionarios: FuncionarioFormacao[];
  unidades: UnidadeOrganicaGF[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  reload: () => Promise<void>;
  addFuncionario: (input: NovoFuncionarioFormacaoInput) => Promise<boolean>;
  updateFuncionario: (id: string, input: Partial<NovoFuncionarioFormacaoInput>) => Promise<boolean>;
  deleteFuncionario: (id: string) => Promise<boolean>;
}

const FuncionariosFormacaoContext = createContext<FuncionariosFormacaoContextValue | null>(null);

function toFuncionarioFormacao(row: Record<string, unknown>): FuncionarioFormacao {
  // filtrar apenas IDs UUID válidos (36 chars) — ignorar corrompidos
  const id = row.id as string;
  if (!id || id.length > 36) return null as unknown as FuncionarioFormacao;
  return {
    id,
    nome: (row.nome as string) ?? '',
    carreira: (row.carreira as FuncionarioFormacao['carreira']) ?? 'Técnica Superior',
    sexo: (row.sexo as 'M' | 'F') ?? 'M',
    unidadeOrganica: (row.unidade_organica as string) ?? '',
    subunidadeOrganica: (row.subunidade_organica as string) ?? '',
    email: (row.email as string) ?? '',
    telefone: (row.telefone as string) ?? '',
    categoria: (row.categoria as string) || '',
    dataAlteracaoCategoria: (row.data_alteracao_categoria as string) || '',
    superiorHierarquico: (row.superior_hierarquico as string) || undefined,
  };
}

function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export function FuncionariosFormacaoProvider({ children }: { children: ReactNode }) {
  const { user, profileLoading } = useAuth();
  const [funcionarios, setFuncionarios] = useState<FuncionarioFormacao[]>([]);
  const [unidades, setUnidades] = useState<UnidadeOrganicaGF[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!user) {
      setFuncionarios([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [funcRes, unidRes] = await Promise.all([
        supabase
          .from('funcionarios')
          .select('id, nome, carreira, sexo, unidade_organica, subunidade_organica, email, telefone, status, categoria, data_alteracao_categoria, superior_hierarquico')
          .eq('municipio_id', MUNICIPIO_FORMACAO_ID)
          .order('nome'),
        supabase
          .from('unidades_organicas')
          .select('id, nome, tipo')
          .eq('municipio_id', MUNICIPIO_FORMACAO_ID)
          .order('ordem'),
      ]);
      if (funcRes.error) throw funcRes.error;
      if (unidRes.error) throw unidRes.error;

      const mapped = (funcRes.data ?? [])
        .map(toFuncionarioFormacao)
        .filter((f): f is FuncionarioFormacao => f !== null && f.id.length <= 36);

      setFuncionarios(mapped);
      setUnidades((unidRes.data ?? []) as UnidadeOrganicaGF[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setFuncionarios([]);
      setLoading(false);
      return;
    }
    if (profileLoading) return;
    reload();
  }, [user, profileLoading, reload]);

  const addFuncionario = useCallback(async (input: NovoFuncionarioFormacaoInput): Promise<boolean> => {
    setSaving(true);
    try {
      const { error: err } = await supabase.from('funcionarios').insert({
        id: generateId(),
        nome: input.nome,
        carreira: input.carreira,
        sexo: input.sexo,
        unidade_organica: input.unidadeOrganica,
        subunidade_organica: input.subunidadeOrganica,
        email: input.email || null,
        telefone: input.telefone || null,
        status: input.status || 'ativo',
        municipio_id: MUNICIPIO_FORMACAO_ID,
        updated_at: new Date().toISOString(),
      });
      if (err) throw err;
      await reload();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao adicionar');
      return false;
    } finally {
      setSaving(false);
    }
  }, [reload]);

  const updateFuncionario = useCallback(async (id: string, input: Partial<NovoFuncionarioFormacaoInput>): Promise<boolean> => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
        municipio_id: MUNICIPIO_FORMACAO_ID,
      };
      if (input.nome !== undefined) payload.nome = input.nome;
      if (input.carreira !== undefined) payload.carreira = input.carreira;
      if (input.sexo !== undefined) payload.sexo = input.sexo;
      if (input.unidadeOrganica !== undefined) payload.unidade_organica = input.unidadeOrganica;
      if (input.subunidadeOrganica !== undefined) payload.subunidade_organica = input.subunidadeOrganica;
      if (input.email !== undefined) payload.email = input.email || null;
      if (input.telefone !== undefined) payload.telefone = input.telefone || null;
      if (input.status !== undefined) payload.status = input.status;
      if (input.dataAlteracaoCategoria !== undefined) payload.data_alteracao_categoria = input.dataAlteracaoCategoria || null;

      const { error: err } = await supabase
        .from('funcionarios')
        .update(payload)
        .eq('id', id)
        .eq('municipio_id', MUNICIPIO_FORMACAO_ID);
      if (err) throw err;
      await reload();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao actualizar');
      return false;
    } finally {
      setSaving(false);
    }
  }, [reload]);

  const deleteFuncionario = useCallback(async (id: string): Promise<boolean> => {
    setSaving(true);
    try {
      const { error: err } = await supabase
        .from('funcionarios')
        .delete()
        .eq('id', id)
        .eq('municipio_id', MUNICIPIO_FORMACAO_ID);
      if (err) throw err;
      setFuncionarios((prev) => prev.filter((f) => f.id !== id));
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao eliminar');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return (
    <FuncionariosFormacaoContext.Provider value={{
      funcionarios, unidades, loading, saving, error,
      reload, addFuncionario, updateFuncionario, deleteFuncionario,
    }}>
      {children}
    </FuncionariosFormacaoContext.Provider>
  );
}

export function useFuncionariosFormacao(): FuncionariosFormacaoContextValue {
  const ctx = useContext(FuncionariosFormacaoContext);
  if (!ctx) throw new Error('useFuncionariosFormacao must be used within FuncionariosFormacaoProvider');
  return ctx;
}
